if (!customElements.get("country-redirect-popup")) {
  class CountryRedirectPopup extends HTMLElement {
    static COOKIE_NAME = "symbyx_country_redirect_dismissed";
    static COOKIE_MAX_AGE_SEC = 86400;

    connectedCallback() {
      if (document.querySelectorAll("country-redirect-popup").length > 1) {
        this.remove();
        return;
      }

      this.modal = this.querySelector("modal-dialog");
      this.stayButton = this.querySelector("[data-country-redirect-stay]");
      this.goButton = this.querySelector("[data-country-redirect-go]");
      this.closeButton = this.querySelector("[data-country-redirect-close]");

      this.stayButton?.addEventListener("click", () => this.dismiss());
      this.closeButton?.addEventListener("click", () => this.dismiss());
      this.goButton?.addEventListener("click", () => this.goToSuggestedStore());

      if (this.dataset.enabled !== "true") return;

      const start = () => {
        if (this.modal) this.bindModalHide();
        if (this.shouldForcePreview()) {
          this.modal?.show();
          return;
        }
        if (this.getCookie()) return;
        this.evaluateAndShow();
      };

      if (customElements.get("modal-dialog")) {
        start();
      } else {
        customElements.whenDefined("modal-dialog").then(start);
      }
    }

    bindModalHide() {
      const originalHide = this.modal.hide.bind(this.modal);
      this.modal.hide = () => {
        if (this.modal.hasAttribute("open")) this.setCookie();
        originalHide();
      };
    }

    shouldForcePreview() {
      return new URLSearchParams(window.location.search).has(
        "country_redirect_preview"
      );
    }

    get storeRegion() {
      return (this.dataset.storeRegion || "").toLowerCase();
    }

    getCookie() {
      const match = document.cookie.match(
        new RegExp(`(?:^|; )${CountryRedirectPopup.COOKIE_NAME}=([^;]*)`)
      );
      return match ? decodeURIComponent(match[1]) : null;
    }

    setCookie() {
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${CountryRedirectPopup.COOKIE_NAME}=1; path=/; max-age=${CountryRedirectPopup.COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
    }

    normalizeCountryCode(code) {
      if (!code) return null;
      const normalized = String(code).trim().toUpperCase();
      return normalized.length === 2 ? normalized : null;
    }

    async detectFromShopify() {
      const root = window.Shopify?.routes?.root || "/";
      const shopifyCountry = window.Shopify?.country;
      const exclude = shopifyCountry
        ? `&country[exclude]=${encodeURIComponent(shopifyCountry)}`
        : "";
      const url = `${root}browsing_context_suggestions.json?country[enabled]=true${exclude}`;

      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) return null;
        const data = await response.json();
        const country = data?.detected_values?.country;
        if (!country) return null;
        return this.normalizeCountryCode(country.iso_code || country.handle);
      } catch {
        return null;
      }
    }

    async detectFromCloudflare() {
      try {
        const response = await fetch("/cdn-cgi/trace", { credentials: "same-origin" });
        if (!response.ok) return null;
        const text = await response.text();
        const match = text.match(/^loc=([A-Za-z]{2})$/m);
        return match ? this.normalizeCountryCode(match[1]) : null;
      } catch {
        return null;
      }
    }

    async detectFromIpApi() {
      try {
        const response = await fetch("https://ipapi.co/country_code/", {
          credentials: "omit",
        });
        if (!response.ok) return null;
        const text = await response.text();
        return this.normalizeCountryCode(text);
      } catch {
        return null;
      }
    }

    async detectVisitorCountry() {
      const detectors = [
        () => this.detectFromShopify(),
        () => this.detectFromCloudflare(),
        () => this.detectFromIpApi(),
      ];

      for (const detect of detectors) {
        const country = await detect();
        if (country) return country;
      }

      return null;
    }

    shouldShowForCountry(countryCode) {
      if (!countryCode) return false;

      if (this.storeRegion === "us" && countryCode === "AU") return true;
      if (this.storeRegion === "au" && countryCode === "US") return true;

      return false;
    }

    async evaluateAndShow() {
      if (!this.storeRegion || !this.modal) return;

      const visitorCountry = await this.detectVisitorCountry();
      if (!this.shouldShowForCountry(visitorCountry)) return;

      this.modal.show();
    }

    dismiss() {
      this.setCookie();
      this.modal?.hide();
    }

    goToSuggestedStore() {
      const targetBase = (this.goButton?.dataset.redirectUrl || "").trim();
      if (!targetBase) {
        this.dismiss();
        return;
      }

      this.setCookie();

      let url = targetBase.replace(/\/$/, "");
      if (this.dataset.preservePath === "true") {
        url += window.location.pathname;
        if (window.location.search) url += window.location.search;
      }

      window.location.assign(url);
    }
  }

  customElements.define("country-redirect-popup", CountryRedirectPopup);
}
